import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  Button,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as BackIcon,
  Dashboard as OverviewIcon,
  Description as DevisIcon,
  LocalShipping as BonLivraisonIcon,
  Receipt as FactureIcon,
  AssignmentReturn as FactureAvoirIcon,
  RequestQuote as FactureProformaIcon,
  AccountBalance as CompteClientIcon,
  Settings as ConfigIcon,
  People as ClientsIcon,
  Payment as ModePaiementIcon,
  CreditScore as ConditionPaiementIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationsBell from '../../components/NotificationsBell';
import HeaderChatButton from '../../components/HeaderChatButton';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

const CommercialLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleBackToMain = () => {
    navigate('/dashboard');
  };

  const commercialMenuItems = [
    {
      text: 'Tableau de bord',
      icon: <OverviewIcon />,
      path: '/commercial',
      exact: true,
      miniModuleName: 'Devis',
    },
    {
      text: 'Devis',
      icon: <DevisIcon />,
      path: '/commercial/devis',
      miniModuleName: 'Devis',
    },
    {
      text: 'Bons de Livraison',
      icon: <BonLivraisonIcon />,
      path: '/commercial/bons-livraison',
      miniModuleName: 'Bons Livraison',
    },
    {
      text: 'Factures',
      icon: <FactureIcon />,
      path: '/commercial/factures',
      miniModuleName: 'Factures',
    },
    {
      text: 'Factures Avoir',
      icon: <FactureAvoirIcon />,
      path: '/commercial/factures-avoir',
      miniModuleName: 'Factures Avoir',
    },
    {
      text: 'Factures Proforma',
      icon: <FactureProformaIcon />,
      path: '/commercial/factures-proforma',
      miniModuleName: 'Factures Proforma',
    },
    {
      text: 'Compte Client',
      icon: <CompteClientIcon />,
      path: '/commercial/compte-client',
      miniModuleName: 'Compte Client',
    },
  ];

  const configMenuItems = [
    {
      text: 'Gestion des Clients',
      icon: <ClientsIcon />,
      path: '/commercial/clients',
      miniModuleName: 'Clients',
    },
    {
      text: 'Conditions de Paiement',
      icon: <ConditionPaiementIcon />,
      path: '/commercial/conditions-paiement',
      miniModuleName: 'Conditions Paiement',
    },
    {
      text: 'Modes de Paiement',
      icon: <ModePaiementIcon />,
      path: '/commercial/modes-paiement',
      miniModuleName: 'Modes Paiement',
    },
  ];

  const menuItemsWithPermissions = commercialMenuItems.map((item) => {
    if (!item.miniModuleName) return { ...item, hasAccess: true };
    const hasAccess = hasPermission('Commercial', 'view', item.miniModuleName);
    return { ...item, hasAccess };
  });

  const configItemsWithPermissions = configMenuItems.map((item) => {
    const hasAccess = hasPermission('Config', 'view', item.miniModuleName);
    return { ...item, hasAccess };
  });

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    // Check exact match or path followed by /
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const drawer = (
    <div>
      <Toolbar>
        {desktopOpen ? (
          <Button
            fullWidth
            startIcon={<BackIcon />}
            onClick={handleBackToMain}
            variant="outlined"
            size="small"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'flex-start' }}
          >
            Retour au Menu Principal
          </Button>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <IconButton onClick={handleBackToMain} size="small">
              <BackIcon />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />

      <List>
        {menuItemsWithPermissions.map((item) => {
          const isDisabled = !item.hasAccess;
          const isActive = !isDisabled && isActiveRoute(item.path, item.exact);
          const tooltipTitle = !item.hasAccess ? 'Accès refusé' : '';

          return (
            <ListItem key={item.text} disablePadding>
              <Tooltip title={tooltipTitle} placement="right" arrow>
                <span style={{ width: '100%' }}>
                  <ListItemButton
                    selected={isActive}
                    onClick={!isDisabled ? () => navigate(item.path) : undefined}
                    disabled={isDisabled}
                    sx={{
                      minHeight: 48,
                      justifyContent: desktopOpen ? 'initial' : 'center',
                      px: desktopOpen ? 2.5 : 0,
                      '&.Mui-selected': {
                        backgroundColor: '#81C784',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#4CAF50',
                        },
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                      },
                      cursor: !isDisabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: desktopOpen ? 3 : 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: isActive ? 'white' : !isDisabled ? 'inherit' : 'text.disabled',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {desktopOpen && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 400,
                          color: !isDisabled ? 'inherit' : 'text.disabled',
                        }}
                      />
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          );
        })}

        {/* Configuration - Collapsible */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setConfigOpen(!configOpen)}
            sx={{
              minHeight: 48,
              justifyContent: desktopOpen ? 'initial' : 'center',
              px: desktopOpen ? 2.5 : 0,
            }}
          >
            <ListItemIcon sx={{
              minWidth: 0,
              mr: desktopOpen ? 3 : 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <ConfigIcon />
            </ListItemIcon>
            {desktopOpen && (
              <>
                <ListItemText
                  primary="Configuration"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
                {configOpen ? <ExpandLess /> : <ExpandMore />}
              </>
            )}
          </ListItemButton>
        </ListItem>

        <Collapse in={configOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {configItemsWithPermissions.map((item) => {
              const isDisabled = !item.hasAccess;
              const isActive = !isDisabled && isActiveRoute(item.path);
              const tooltipTitle = !item.hasAccess ? 'Accès refusé' : '';

              return (
                <ListItem key={item.text} disablePadding>
                  <Tooltip title={tooltipTitle} placement="right" arrow>
                    <span style={{ width: '100%' }}>
                      <ListItemButton
                        selected={isActive}
                        onClick={!isDisabled ? () => navigate(item.path) : undefined}
                        disabled={isDisabled}
                        sx={{
                          minHeight: 44,
                          justifyContent: desktopOpen ? 'initial' : 'center',
                          pl: desktopOpen ? 4 : 0,
                          pr: desktopOpen ? 2.5 : 0,
                          '&.Mui-selected': {
                            backgroundColor: '#81C784',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#4CAF50',
                            },
                          },
                          '&.Mui-disabled': {
                            opacity: 0.6,
                            cursor: 'not-allowed',
                          },
                          cursor: !isDisabled ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 0,
                          mr: desktopOpen ? 3 : 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: isActive ? 'white' : 'inherit',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        {desktopOpen && (
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.9rem',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </span>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: {
            sm: desktopOpen
              ? `calc(100% - ${drawerWidth}px)`
              : `calc(100% - ${collapsedDrawerWidth}px)`,
          },
          ml: {
            sm: desktopOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`,
          },
          bgcolor: '#4CAF50',
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Module Commercial
          </Typography>
          <HeaderChatButton />
          <NotificationsBell />
          <ThemeToggle />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        onMouseEnter={() => {
          if (!desktopOpen) setDesktopOpen(true);
        }}
        sx={{
          width: { sm: desktopOpen ? drawerWidth : collapsedDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        onClick={() => {
          if (desktopOpen) setDesktopOpen(false);
        }}
        sx={{
          flexGrow: 1,
          py: 3,
          px: 2,
          width: {
            sm: desktopOpen
              ? `calc(100% - ${drawerWidth}px)`
              : `calc(100% - ${collapsedDrawerWidth}px)`,
          },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default CommercialLayout;
